import debounce from 'lodash/debounce'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

import {
  AutoCompleteAddress,
  FilterAccordion,
  PlaceAutoComplete,
  SearchInput,
} from '@avantstay/backoffice-core'

import { AddressRadiusTag } from '@new/__global/components/Filters/AddressRadiusTag'
import {
  AddressRadiusProps,
  FiltersSharedProps,
  GeolocationProps,
} from '@new/__global/components/Filters/Filters.types'
import { filtersLabels } from '@new/domains/bookings/screens/bookingsList/utils/labels'

import * as S from './Filters.styles'

interface AddressRadiusFilterProps extends FiltersSharedProps {
  initialValue?: AddressRadiusProps
  onChange: (value?: AddressRadiusProps) => void
}

const DEFAULT_RADIUS_INPUT = '5'
export const AddressRadiusFilter: FC<AddressRadiusFilterProps> = ({
  initialValue,
  onChange,
  showFilterAsColumn,
  onShowFilterAsColumn,
}) => {
  const initialRadius = String(initialValue?.radius || DEFAULT_RADIUS_INPUT)
  const initialGeolocation = {
    latitude: initialValue?.latitude,
    longitude: initialValue?.longitude,
  }
  const [searchTerm, setSearchTerm] = useState<string | undefined>(initialValue?.address)
  const [geoLocation, setGeoLocation] = useState<GeolocationProps | undefined>(initialGeolocation)
  const [localRadius, setLocalRadius] = useState<string | undefined>(initialRadius)

  const wrapperRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputOnChange = useCallback(
    debounce((value?: string) => {
      setSearchTerm(value)
    }, 300),
    [],
  )

  useEffect(() => {
    if (searchTerm !== initialValue?.address || localRadius !== initialRadius) {
      debouncedInputOnChange(searchTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  // This clears selection when we run propertiesListStore.resetFilters
  useEffect(() => {
    if (initialValue === undefined || initialValue?.address === '') {
      setSearchTerm('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue])

  const latitude = geoLocation?.latitude
  const longitude = geoLocation?.longitude

  const handleSubmitValue = () => {
    const radius = localRadius ? Number(localRadius) : undefined

    if (searchTerm && latitude && longitude && isValidRadius) {
      onChange({ address: searchTerm, radius, latitude, longitude })
    }
  }

  const isValidRadius = localRadius?.length ? Number(localRadius) > 0 : true

  useEffect(() => {
    if ((latitude && longitude) || isValidRadius) {
      handleSubmitValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, localRadius])

  const handleOnClear = () => {
    setLocalRadius('')
    setSearchTerm('')
    onChange({ address: '', radius: undefined, latitude: undefined, longitude: undefined })
  }

  const filterValue =
    searchTerm || localRadius
      ? `${searchTerm} ${searchTerm && localRadius && '-'} ${localRadius} mi.`
      : undefined

  const temporaryValue =
    !searchTerm && localRadius === DEFAULT_RADIUS_INPUT ? undefined : filterValue

  const handleSelectAutoComplete = (address: AutoCompleteAddress) => {
    setSearchTerm(address.plainAddressText)
    setGeoLocation({ latitude: address.lat, longitude: address.lng })
  }

  return (
    <FilterAccordion
      title={filtersLabels.addressRadius}
      isSidebarFilter
      ref={wrapperRef}
      filterValue={temporaryValue}
      onClearFilter={handleOnClear}
      customFilterTag={<AddressRadiusTag onClearFilter={handleOnClear} fullAddress={filterValue} />}
      onShowFilterAsColumn={onShowFilterAsColumn}
      showFilterAsColumn={showFilterAsColumn}
    >
      <S.RangeInputWrapper flexDirection="column">
        <PlaceAutoComplete
          onChange={event => setSearchTerm(event?.target.value)}
          placeholder="Search Address"
          searchTerm={searchTerm}
          iconSearchSize={{ width: 16, height: 16 }}
          onAutocompleteSelection={handleSelectAutoComplete}
        />
        <SearchInput
          onChange={value => setLocalRadius(value)}
          searchTerm={localRadius}
          beforeValue="Search radius (Mi.)"
          inputTextAlign="right"
          hasSearchIcon={false}
          // Required to align clear icon with search on top
          inputLeftIcon={<></>}
          mask={createNumberMask({
            prefix: '',
            allowDecimal: false,
            allowLeadingZeroes: false,
            allowNegative: false,
          })}
          error={!isValidRadius ? 'Radius must be greater than 0' : undefined}
        />
      </S.RangeInputWrapper>
    </FilterAccordion>
  )
}
